// Procurement application messages (English, Russian and Uzbek).
export const procurementMessages = {
  en: {
    procurement: {
      appTitle: 'Procurement',
      appDescription: 'Purchase orders, suppliers and delivery tracking.',
      nav: {
        overview: 'Overview',
        purchasing: 'Purchasing',
        purchaseOrders: 'Purchase orders',
        suppliers: 'Suppliers',
        invoices: 'Supplier invoices',
        deliveries: 'Deliveries',
      },
      common: {
        loading: 'Loading…',
        retry: 'Retry',
        saving: 'Saving…',
        cancel: 'Cancel',
        delete: 'Delete',
        optional: 'optional',
        clear: 'Clear',
        selectPlaceholder: 'Select…',
      },
      pagination: {
        previous: 'Previous page',
        next: 'Next page',
        range: '{from}–{to} of {total}',
        size: 'Rows per page',
      },
      status: {
        draft: 'Draft',
        approved: 'Approved',
        ordered: 'Ordered',
        partially_received: 'Partially received',
        received: 'Received',
        cancelled: 'Cancelled',
      },
      overview: {
        eyebrow: 'Procurement',
        title: 'Overview',
        description: 'Purchase orders, open deliveries and supplier activity.',
        totalOrders: 'Purchase orders',
        openOrders: 'Open orders',
        overdue: 'Overdue deliveries',
        receivedOrders: 'Received',
        valueByCurrency: 'Committed value by currency',
        recent: 'Recent purchase orders',
        viewAll: 'View all',
        quickOrdersHint: 'Create and track purchase orders',
        quickDeliveriesHint: 'See what is due to arrive',
        quickSuppliersHint: 'Browse the supplier register',
        payables: 'Open invoices',
        quickInvoicesHint: 'Track supplier payables',
        empty: 'Nothing here yet.',
      },
      orders: {
        eyebrow: 'Purchasing',
        title: 'Purchase orders',
        description: 'Every purchase order, its supplier, destination warehouse and status.',
        searchPlaceholder: 'Search by number, supplier, warehouse or requester…',
        allStatuses: 'All statuses',
        allSuppliers: 'All suppliers',
        allWarehouses: 'All warehouses',
        newOrder: 'New order',
        count: '{count} purchase order(s)',
        order: 'Order',
        supplier: 'Supplier',
        warehouse: 'Warehouse',
        status: 'Status',
        expected: 'Expected',
        total: 'Total',
        created: 'Created',
        empty: 'No purchase orders match these filters.',
        open: 'Open',
        page: 'Page {page} of {total}',
      },
      order: {
        eyebrow: 'Purchase order',
        back: 'Purchase orders',
        edit: 'Edit',
        receive: 'Receive',
        delete: 'Delete',
        summary: 'Summary',
        status: 'Status',
        supplier: 'Supplier',
        warehouse: 'Destination warehouse',
        requester: 'Requester',
        currency: 'Currency',
        exchangeRate: 'rate',
        expected: 'Expected date',
        created: 'Created',
        notes: 'Notes',
        items: 'Order lines',
        product: 'Item',
        quantity: 'Quantity',
        received: 'Received',
        remaining: 'Remaining',
        price: 'Unit price',
        lineTotal: 'Line total',
        grandTotal: 'Total',
        noItems: 'This order has no lines.',
        notFound: 'Purchase order not found.',
        deleteTitle: 'Delete purchase order',
        deleteConfirm: 'This removes the purchase order. Continue?',
      },
      form: {
        createTitle: 'New purchase order',
        editTitle: 'Edit purchase order #{id}',
        description: 'Pick a supplier and currency, then add the items to order.',
        back: 'Purchase orders',
        heading: 'Order details',
        selectSupplier: 'Select supplier',
        selectWarehouse: 'Select warehouse',
        supplier: 'Supplier',
        warehouse: 'Destination warehouse',
        warehouseHint: 'Where the goods will be received. Can be set later at receiving time.',
        currency: 'Currency',
        exchangeRate: 'Exchange rate',
        expectedDate: 'Expected date',
        status: 'Status',
        statusHint: 'Draft, approved and ordered are typical before goods arrive.',
        notes: 'Notes',
        notesPlaceholder: 'Optional notes for this order',
        requesterNote: 'Requested by {name}.',
        lines: 'Order lines',
        addLine: 'Add item',
        product: 'Item',
        quantity: 'Quantity',
        price: 'Unit price',
        lineTotal: 'Line total',
        removeLine: 'Remove',
        addUom: 'Add unit',
        detailedUom: 'Multi-UOM quantities',
        apply: 'Apply',
        totalInBase: 'Base total',
        pricePerBase: 'Unit price (per base unit)',
        totalAmount: 'Total',
        create: 'Create',
        update: 'Save',
        validationSupplier: 'Select a supplier.',
        validationCurrency: 'Select a currency.',
        validationItems: 'Add at least one order line.',
        validationProduct: 'A line has no item.',
        validationQuantity: 'Every quantity must be greater than zero.',
        validationPrice: 'Every unit price must be greater than zero.',
        missingUser: 'No signed-in user to record as requester.',
      },
      receive: {
        title: 'Receive goods',
        description: 'Receiving writes an inbound purchase movement into the destination warehouse.',
        warehouse: 'Receiving warehouse',
        warehouseHint: 'The warehouse that takes the goods in.',
        receiveAll: 'Receive all remaining quantities',
        receiveAllHint: 'The backend receives everything the order still expects.',
        specificHint: 'Enter what actually arrived. Quantities must not exceed what is still outstanding.',
        ordered: 'ordered',
        received: 'received',
        remaining: 'remaining',
        now: 'Receiving now',
        submit: 'Receive',
        submitting: 'Receiving…',
        success: 'Goods received. The order status was updated.',
        validationRemaining: '{product}: at most {quantity} can still be received.',
        validationEmpty: 'Enter a quantity for at least one line.',
        lotTitle: 'Lot',
        lotButton: 'Lot',
        lotNumber: 'Lot number',
        lotNumberPlaceholder: 'e.g. LOT-2026-001',
        supplierBatch: 'Supplier batch',
        productionBatch: 'Production batch',
        manufacturedAt: 'Manufactured',
        expiresAt: 'Expires',
        qualityStatus: 'Quality',
        lotHint: 'Optional. An existing product lot is reused, otherwise a new one is created with these details.',
        lotNeedsQuantities: 'Enter quantities per line to record lot, batch or expiry details.',
        successWithDocument: 'Goods received. Receipt document {document} was posted.',
      },
      lotQuality: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        quarantined: 'Quarantined',
      },
      suppliers: {
        eyebrow: 'Procurement',
        title: 'Suppliers',
        description: 'Suppliers available for purchase orders.',
        searchPlaceholder: 'Search suppliers…',
        count: '{count} supplier(s)',
        hint: 'The supplier register is managed in the Admin application.',
        empty: 'No suppliers found.',
        name: 'Supplier',
        code: 'ID',
      },
      deliveries: {
        eyebrow: 'Procurement',
        title: 'Deliveries',
        description: 'Purchase orders awaiting or already receiving goods.',
        searchPlaceholder: 'Search by number, supplier or warehouse…',
        onlyOpen: 'Only open orders',
        count: '{count} order(s)',
        empty: 'No deliveries match these filters.',
        order: 'Order',
        supplier: 'Supplier',
        expected: 'Expected',
        status: 'Status',
        overdue: 'Overdue',
        dueSoon: 'Due soon',
      },
      receipts: {
        title: 'Receipts',
        count: '{count} movement(s)',
        hint: 'Inbound purchase movements booked against this order.',
        date: 'Date',
        item: 'Item',
        quantity: 'Quantity',
        warehouse: 'Warehouse',
        lot: 'Lot',
        noLot: 'No lot',
        empty: 'Nothing has been received for this order yet.',
        document: 'Document',
      },
      invoiceStatus: {
        open: 'Open',
        partial: 'Partial',
        closed: 'Closed',
        void: 'Void',
      },
      invoices: {
        eyebrow: 'Procurement',
        title: 'Supplier invoices',
        description: 'Payables recorded against suppliers, with their settlement status.',
        searchPlaceholder: 'Search by number or supplier…',
        allStatuses: 'All statuses',
        allSuppliers: 'All suppliers',
        allCurrencies: 'All currencies',
        newInvoice: 'New invoice',
        count: '{count} invoice(s)',
        invoice: 'Invoice',
        supplier: 'Supplier',
        order: 'Purchase order',
        invoiceDate: 'Invoice date',
        dueDate: 'Due date',
        amount: 'Amount',
        status: 'Status',
        open: 'Open',
        overdue: 'Overdue',
        void: 'Void',
        empty: 'No supplier invoices match these filters.',
        hint: 'Settlement status comes from payment allocations; only voiding is done by hand.',
      },
      invoice: {
        back: 'Supplier invoices',
        edit: 'Edit',
        void: 'Void',
        delete: 'Delete',
        summary: 'Summary',
        supplier: 'Supplier',
        order: 'Purchase order',
        invoiceNumber: 'Invoice number',
        invoiceDate: 'Invoice date',
        dueDate: 'Due date',
        currency: 'Currency',
        exchangeRate: 'rate',
        amount: 'Invoice amount',
        status: 'Status',
        department: 'Department',
        warehouse: 'Warehouse',
        notes: 'Notes',
        created: 'Created',
        updated: 'Updated',
        notFound: 'Supplier invoice not found.',
        openOrder: 'Open order',
        voidTitle: 'Void invoice',
        voidConfirm: 'The invoice will be marked as void. Continue?',
        deleteTitle: 'Delete invoice',
        deleteConfirm: 'This removes the supplier invoice. Continue?',
        voidSuccess: 'Invoice voided.',
      },
      invoiceForm: {
        createTitle: 'New supplier invoice',
        editTitle: 'Edit invoice #{id}',
        description: 'Record the supplier invoice and, when there is one, the purchase order it settles.',
        back: 'Supplier invoices',
        heading: 'Invoice details',
        selectSupplier: 'Select supplier',
        selectWarehouse: 'Select warehouse',
        selectDepartment: 'Select department',
        supplier: 'Supplier',
        purchaseOrder: 'Purchase order',
        purchaseOrderHint: 'Only orders of the selected supplier with the same currency qualify.',
        noOrders: 'No matching purchase order',
        invoiceNumber: 'Invoice number',
        invoiceNumberPlaceholder: 'For example INV-ACME-1001',
        invoiceDate: 'Invoice date',
        dueDate: 'Due date',
        currency: 'Currency',
        exchangeRate: 'Exchange rate',
        amount: 'Invoice amount',
        department: 'Department',
        warehouse: 'Warehouse',
        notes: 'Notes',
        notesPlaceholder: 'Optional notes for this invoice',
        status: 'Status',
        statusHint: 'New invoices are recorded as open. Settlement is driven by payment allocations.',
        voidHint: 'Only voiding is allowed from this form; open, partial and closed come from payment allocations.',
        create: 'Create',
        update: 'Save',
        validationSupplier: 'Select a supplier.',
        validationCurrency: 'Select a currency.',
        validationNumber: 'Enter the invoice number.',
        validationInvoiceDate: 'Enter the invoice date.',
        validationDueDate: 'The due date cannot be before the invoice date.',
        validationAmount: 'The invoice amount must be greater than zero.',
      },
      picker: {
        selectProduct: 'Select item',
        agentEyebrow: 'Agents',
        agentSearchLabel: 'Search agents',
        agentSearchPlaceholder: 'Search by name or phone…',
        agentEmpty: 'No agents found.',
        agentLoading: 'Loading agents…',
        searchLabel: 'Search items',
        searchPlaceholder: 'Search by name or SKU…',
        empty: 'No items found.',
        loading: 'Loading items…',
        page: 'Page {page} of {total}',
        close: 'Close',
        retry: 'Retry',
        previous: 'Previous',
        next: 'Next',
        cancel: 'Cancel',
      },
    },
  },
  ru: {
    procurement: {
      appTitle: 'Закупки',
      appDescription: 'Заказы на закупку, поставщики и отслеживание поставок.',
      nav: {
        overview: 'Обзор',
        purchasing: 'Закупки',
        purchaseOrders: 'Заказы на закупку',
        suppliers: 'Поставщики',
        invoices: 'Счета поставщиков',
        deliveries: 'Поставки',
      },
      common: {
        loading: 'Загрузка…',
        retry: 'Повторить',
        saving: 'Сохранение…',
        cancel: 'Отмена',
        delete: 'Удалить',
        optional: 'необязательно',
        clear: 'Очистить',
        selectPlaceholder: 'Выберите…',
      },
      pagination: {
        previous: 'Предыдущая страница',
        next: 'Следующая страница',
        range: '{from}–{to} из {total}',
        size: 'Строк на странице',
      },
      status: {
        draft: 'Черновик',
        approved: 'Согласован',
        ordered: 'Заказан',
        partially_received: 'Частично получен',
        received: 'Получен',
        cancelled: 'Отменён',
      },
      overview: {
        eyebrow: 'Закупки',
        title: 'Обзор',
        description: 'Заказы на закупку, открытые поставки и активность поставщиков.',
        totalOrders: 'Заказы на закупку',
        openOrders: 'Открытые заказы',
        overdue: 'Просроченные поставки',
        receivedOrders: 'Получено',
        valueByCurrency: 'Сумма по валютам',
        recent: 'Последние заказы',
        viewAll: 'Показать все',
        quickOrdersHint: 'Создание и отслеживание заказов',
        quickDeliveriesHint: 'Что должно прибыть',
        quickSuppliersHint: 'Реестр поставщиков',
        payables: 'Открытые счета',
        quickInvoicesHint: 'Задолженность поставщикам',
        empty: 'Пока ничего нет.',
      },
      orders: {
        eyebrow: 'Закупки',
        title: 'Заказы на закупку',
        description: 'Все заказы, поставщик, склад назначения и статус.',
        searchPlaceholder: 'Поиск по номеру, поставщику, складу или инициатору…',
        allStatuses: 'Все статусы',
        allSuppliers: 'Все поставщики',
        allWarehouses: 'Все склады',
        newOrder: 'Новый заказ',
        count: '{count} заказ(ов)',
        order: 'Заказ',
        supplier: 'Поставщик',
        warehouse: 'Склад',
        status: 'Статус',
        expected: 'Ожидается',
        total: 'Сумма',
        created: 'Создан',
        empty: 'Нет заказов по этим фильтрам.',
        open: 'Открыть',
        page: 'Страница {page} из {total}',
      },
      order: {
        eyebrow: 'Заказ на закупку',
        back: 'Заказы на закупку',
        edit: 'Изменить',
        receive: 'Принять',
        delete: 'Удалить',
        summary: 'Сводка',
        status: 'Статус',
        supplier: 'Поставщик',
        warehouse: 'Склад назначения',
        requester: 'Инициатор',
        currency: 'Валюта',
        exchangeRate: 'курс',
        expected: 'Ожидаемая дата',
        created: 'Создан',
        notes: 'Примечания',
        items: 'Позиции заказа',
        product: 'Товар',
        quantity: 'Количество',
        received: 'Получено',
        remaining: 'Остаток',
        price: 'Цена за единицу',
        lineTotal: 'Сумма строки',
        grandTotal: 'Итого',
        noItems: 'В заказе нет позиций.',
        notFound: 'Заказ не найден.',
        deleteTitle: 'Удалить заказ',
        deleteConfirm: 'Заказ будет удалён. Продолжить?',
      },
      form: {
        createTitle: 'Новый заказ на закупку',
        editTitle: 'Изменение заказа #{id}',
        description: 'Выберите поставщика и валюту, затем добавьте позиции заказа.',
        back: 'Заказы на закупку',
        heading: 'Данные заказа',
        selectSupplier: 'Выберите поставщика',
        selectWarehouse: 'Выберите склад',
        supplier: 'Поставщик',
        warehouse: 'Склад назначения',
        warehouseHint: 'Куда будет принят товар. Можно указать позже при приёмке.',
        currency: 'Валюта',
        exchangeRate: 'Курс обмена',
        expectedDate: 'Ожидаемая дата',
        status: 'Статус',
        statusHint: 'До приёмки обычно используются черновик, согласован или заказан.',
        notes: 'Примечания',
        notesPlaceholder: 'Необязательные примечания к заказу',
        requesterNote: 'Инициатор: {name}.',
        lines: 'Позиции заказа',
        addLine: 'Добавить товар',
        product: 'Товар',
        quantity: 'Количество',
        price: 'Цена за единицу',
        lineTotal: 'Сумма строки',
        removeLine: 'Удалить',
        addUom: 'Добавить единицу',
        detailedUom: 'Количество по единицам',
        apply: 'Применить',
        totalInBase: 'Итого в базовых',
        pricePerBase: 'Цена за базовую единицу',
        totalAmount: 'Итого',
        create: 'Создать',
        update: 'Сохранить',
        validationSupplier: 'Выберите поставщика.',
        validationCurrency: 'Выберите валюту.',
        validationItems: 'Добавьте хотя бы одну позицию.',
        validationProduct: 'В строке не выбран товар.',
        validationQuantity: 'Количество должно быть больше нуля.',
        validationPrice: 'Цена должна быть больше нуля.',
        missingUser: 'Нет пользователя для указания инициатора.',
      },
      receive: {
        title: 'Приёмка товара',
        description: 'Приёмка создаёт входящее движение закупки на склад назначения.',
        warehouse: 'Склад приёмки',
        warehouseHint: 'Склад, который принимает товар.',
        receiveAll: 'Принять весь остаток',
        receiveAllHint: 'Сервер примет всё, что ещё ожидается по заказу.',
        specificHint: 'Введите фактически прибывшее количество. Оно не должно превышать остаток.',
        ordered: 'заказано',
        received: 'получено',
        remaining: 'остаток',
        now: 'Принимаем сейчас',
        submit: 'Принять',
        submitting: 'Приёмка…',
        success: 'Товар принят. Статус заказа обновлён.',
        validationRemaining: '{product}: можно принять не более {quantity}.',
        validationEmpty: 'Укажите количество хотя бы в одной строке.',
        lotTitle: 'Партия',
        lotButton: 'Партия',
        lotNumber: 'Номер партии',
        lotNumberPlaceholder: 'например LOT-2026-001',
        supplierBatch: 'Партия поставщика',
        productionBatch: 'Производственная партия',
        manufacturedAt: 'Изготовлено',
        expiresAt: 'Годен до',
        qualityStatus: 'Качество',
        lotHint: 'Необязательно. Существующая партия товара переиспользуется, иначе создаётся новая с этими данными.',
        lotNeedsQuantities: 'Введите количество по строкам, чтобы указать партию, батч или срок годности.',
        successWithDocument: 'Товар принят. Создан документ приёмки {document}.',
      },
      lotQuality: {
        pending: 'Ожидает',
        approved: 'Одобрено',
        rejected: 'Отклонено',
        quarantined: 'Карантин',
      },
      suppliers: {
        eyebrow: 'Закупки',
        title: 'Поставщики',
        description: 'Поставщики, доступные для заказов на закупку.',
        searchPlaceholder: 'Поиск поставщиков…',
        count: '{count} поставщик(ов)',
        hint: 'Реестр поставщиков ведётся в приложении «Администрирование».',
        empty: 'Поставщики не найдены.',
        name: 'Поставщик',
        code: 'ID',
      },
      deliveries: {
        eyebrow: 'Закупки',
        title: 'Поставки',
        description: 'Заказы, ожидающие или уже принимающие товар.',
        searchPlaceholder: 'Поиск по номеру, поставщику или складу…',
        onlyOpen: 'Только открытые',
        count: '{count} заказ(ов)',
        empty: 'Нет поставок по этим фильтрам.',
        order: 'Заказ',
        supplier: 'Поставщик',
        expected: 'Ожидается',
        status: 'Статус',
        overdue: 'Просрочено',
        dueSoon: 'Скоро',
      },
      receipts: {
        title: 'Приёмки',
        count: '{count} движен.',
        hint: 'Входящие движения закупки по этому заказу.',
        date: 'Дата',
        item: 'Товар',
        quantity: 'Количество',
        warehouse: 'Склад',
        lot: 'Партия',
        noLot: 'Без партии',
        empty: 'По этому заказу ещё ничего не принято.',
        document: 'Документ',
      },
      invoiceStatus: {
        open: 'Открыт',
        partial: 'Частично',
        closed: 'Закрыт',
        void: 'Аннулирован',
      },
      invoices: {
        eyebrow: 'Закупки',
        title: 'Счета поставщиков',
        description: 'Задолженность перед поставщиками и статус расчётов.',
        searchPlaceholder: 'Поиск по номеру или поставщику…',
        allStatuses: 'Все статусы',
        allSuppliers: 'Все поставщики',
        allCurrencies: 'Все валюты',
        newInvoice: 'Новый счёт',
        count: '{count} счёт(ов)',
        invoice: 'Счёт',
        supplier: 'Поставщик',
        order: 'Заказ на закупку',
        invoiceDate: 'Дата счёта',
        dueDate: 'Срок оплаты',
        amount: 'Сумма',
        status: 'Статус',
        open: 'Открытые',
        overdue: 'Просроченные',
        void: 'Аннулировать',
        empty: 'Нет счетов по этим фильтрам.',
        hint: 'Статус расчётов формируется распределением платежей; вручную доступно только аннулирование.',
      },
      invoice: {
        back: 'Счета поставщиков',
        edit: 'Изменить',
        void: 'Аннулировать',
        delete: 'Удалить',
        summary: 'Сводка',
        supplier: 'Поставщик',
        order: 'Заказ на закупку',
        invoiceNumber: 'Номер счёта',
        invoiceDate: 'Дата счёта',
        dueDate: 'Срок оплаты',
        currency: 'Валюта',
        exchangeRate: 'курс',
        amount: 'Сумма счёта',
        status: 'Статус',
        department: 'Отдел',
        warehouse: 'Склад',
        notes: 'Примечания',
        created: 'Создан',
        updated: 'Изменён',
        notFound: 'Счёт не найден.',
        openOrder: 'Открыть заказ',
        voidTitle: 'Аннулировать счёт',
        voidConfirm: 'Счёт будет помечен как аннулированный. Продолжить?',
        deleteTitle: 'Удалить счёт',
        deleteConfirm: 'Счёт будет удалён. Продолжить?',
        voidSuccess: 'Счёт аннулирован.',
      },
      invoiceForm: {
        createTitle: 'Новый счёт поставщика',
        editTitle: 'Изменение счёта #{id}',
        description: 'Укажите счёт поставщика и, при наличии, закрываемый им заказ.',
        back: 'Счета поставщиков',
        heading: 'Данные счёта',
        selectSupplier: 'Выберите поставщика',
        selectWarehouse: 'Выберите склад',
        selectDepartment: 'Выберите отдел',
        supplier: 'Поставщик',
        purchaseOrder: 'Заказ на закупку',
        purchaseOrderHint: 'Подходят только заказы выбранного поставщика с той же валютой.',
        noOrders: 'Подходящего заказа нет',
        invoiceNumber: 'Номер счёта',
        invoiceNumberPlaceholder: 'Например INV-ACME-1001',
        invoiceDate: 'Дата счёта',
        dueDate: 'Срок оплаты',
        currency: 'Валюта',
        exchangeRate: 'Курс обмена',
        amount: 'Сумма счёта',
        department: 'Отдел',
        warehouse: 'Склад',
        notes: 'Примечания',
        notesPlaceholder: 'Необязательные примечания к счёту',
        status: 'Статус',
        statusHint: 'Новые счета создаются открытыми; расчёты формируются распределением платежей.',
        voidHint: 'Из этой формы доступно только аннулирование; открытый, частичный и закрытый статусы приходят из платежей.',
        create: 'Создать',
        update: 'Сохранить',
        validationSupplier: 'Выберите поставщика.',
        validationCurrency: 'Выберите валюту.',
        validationNumber: 'Укажите номер счёта.',
        validationInvoiceDate: 'Укажите дату счёта.',
        validationDueDate: 'Срок оплаты не может быть раньше даты счёта.',
        validationAmount: 'Сумма счёта должна быть больше нуля.',
      },
      picker: {
        selectProduct: 'Выберите товар',
        agentEyebrow: 'Агенты',
        agentSearchLabel: 'Поиск агентов',
        agentSearchPlaceholder: 'Поиск по названию или телефону…',
        agentEmpty: 'Агенты не найдены.',
        agentLoading: 'Загрузка агентов…',
        searchLabel: 'Поиск товаров',
        searchPlaceholder: 'Поиск по названию или артикулу…',
        empty: 'Товары не найдены.',
        loading: 'Загрузка товаров…',
        page: 'Страница {page} из {total}',
        close: 'Закрыть',
        retry: 'Повторить',
        previous: 'Назад',
        next: 'Вперёд',
        cancel: 'Отмена',
      },
    },
  },
  uz: {
    procurement: {
      appTitle: 'Xaridlar',
      appDescription: 'Xarid buyurtmalari, yetkazib beruvchilar va yetkazib berish nazorati.',
      nav: {
        overview: 'Umumiy korinish',
        purchasing: 'Xaridlar',
        purchaseOrders: 'Xarid buyurtmalari',
        suppliers: 'Yetkazib beruvchilar',
        invoices: 'Yetkazib beruvchi schotlari',
        deliveries: 'Yetkazib berishlar',
      },
      common: {
        loading: 'Yuklanmoqda…',
        retry: 'Qayta urinish',
        saving: 'Saqlanmoqda…',
        cancel: 'Bekor qilish',
        delete: 'Ochirish',
        optional: 'ixtiyoriy',
        clear: 'Tozalash',
        selectPlaceholder: 'Tanlang…',
      },
      pagination: {
        previous: 'Oldingi sahifa',
        next: 'Keyingi sahifa',
        range: '{total} dan {from}–{to}',
        size: 'Sahifadagi qatorlar',
      },
      status: {
        draft: 'Qoralama',
        approved: 'Tasdiqlangan',
        ordered: 'Buyurtma qilingan',
        partially_received: 'Qisman qabul qilingan',
        received: 'Qabul qilingan',
        cancelled: 'Bekor qilingan',
      },
      overview: {
        eyebrow: 'Xaridlar',
        title: 'Umumiy korinish',
        description: 'Xarid buyurtmalari, ochiq yetkazib berishlar va yetkazib beruvchilar.',
        totalOrders: 'Xarid buyurtmalari',
        openOrders: 'Ochiq buyurtmalar',
        overdue: 'Kechikkan yetkazib berishlar',
        receivedOrders: 'Qabul qilingan',
        valueByCurrency: 'Valyuta boyicha summa',
        recent: 'Songgi buyurtmalar',
        viewAll: 'Barchasini korish',
        quickOrdersHint: 'Buyurtmalarni yaratish va kuzatish',
        quickDeliveriesHint: 'Nima kelishi kerakligini korish',
        quickSuppliersHint: 'Yetkazib beruvchilar reyestri',
        payables: 'Ochiq schotlar',
        quickInvoicesHint: 'Yetkazib beruvchilarga qarzlar',
        empty: 'Hozircha malumot yoq.',
      },
      orders: {
        eyebrow: 'Xaridlar',
        title: 'Xarid buyurtmalari',
        description: 'Barcha buyurtmalar, yetkazib beruvchi, manzil ombori va holati.',
        searchPlaceholder: 'Raqam, yetkazib beruvchi, ombor yoki talab boyicha qidirish…',
        allStatuses: 'Barcha holatlar',
        allSuppliers: 'Barcha yetkazib beruvchilar',
        allWarehouses: 'Barcha omborlar',
        newOrder: 'Yangi buyurtma',
        count: '{count} buyurtma',
        order: 'Buyurtma',
        supplier: 'Yetkazib beruvchi',
        warehouse: 'Ombor',
        status: 'Holat',
        expected: 'Kutilmoqda',
        total: 'Summa',
        created: 'Yaratilgan',
        empty: 'Bu filtrlar boyicha buyurtma yoq.',
        open: 'Ochish',
        page: '{total} dan {page}-sahifa',
      },
      order: {
        eyebrow: 'Xarid buyurtmasi',
        back: 'Xarid buyurtmalari',
        edit: 'Tahrirlash',
        receive: 'Qabul qilish',
        delete: 'Ochirish',
        summary: 'Malumot',
        status: 'Holat',
        supplier: 'Yetkazib beruvchi',
        warehouse: 'Manzil ombori',
        requester: 'Talab qiluvchi',
        currency: 'Valyuta',
        exchangeRate: 'kurs',
        expected: 'Kutilayotgan sana',
        created: 'Yaratilgan',
        notes: 'Izohlar',
        items: 'Buyurtma qatorlari',
        product: 'Tovar',
        quantity: 'Miqdor',
        received: 'Qabul qilingan',
        remaining: 'Qolgan',
        price: 'Birlik narxi',
        lineTotal: 'Qator summasi',
        grandTotal: 'Jami',
        noItems: 'Buyurtmada qatorlar yoq.',
        notFound: 'Buyurtma topilmadi.',
        deleteTitle: 'Buyurtmani ochirish',
        deleteConfirm: 'Buyurtma ochiriladi. Davom etilsinmi?',
      },
      form: {
        createTitle: 'Yangi xarid buyurtmasi',
        editTitle: '#{id} buyurtmasini tahrirlash',
        description: 'Yetkazib beruvchi va valyutani tanlang, song buyurtma qatorlarini qoshish.',
        back: 'Xarid buyurtmalari',
        heading: 'Buyurtma malumotlari',
        selectSupplier: 'Yetkazib beruvchini tanlang',
        selectWarehouse: 'Omborni tanlang',
        supplier: 'Yetkazib beruvchi',
        warehouse: 'Manzil ombori',
        warehouseHint: 'Tovar qabul qilinadigan ombor. Keyinroq qabul qilishda belgilash mumkin.',
        currency: 'Valyuta',
        exchangeRate: 'Ayirboshlash kursi',
        expectedDate: 'Kutilayotgan sana',
        status: 'Holat',
        statusHint: 'Tovar kelishidan oldin odatda qoralama, tasdiqlangan yoki buyurtma qilingan.',
        notes: 'Izohlar',
        notesPlaceholder: 'Buyurtma uchun ixtiyoriy izohlar',
        requesterNote: 'Talab qiluvchi: {name}.',
        lines: 'Buyurtma qatorlari',
        addLine: 'Tovar qoshish',
        product: 'Tovar',
        quantity: 'Miqdor',
        price: 'Birlik narxi',
        lineTotal: 'Qator summasi',
        removeLine: 'Ochirish',
        addUom: 'Olchov qoshish',
        detailedUom: 'Olchovlar boyicha miqdor',
        apply: 'Qollash',
        totalInBase: 'Asosiy jami',
        pricePerBase: 'Asosiy olchov uchun narx',
        totalAmount: 'Jami',
        create: 'Yaratish',
        update: 'Saqlash',
        validationSupplier: 'Yetkazib beruvchini tanlang.',
        validationCurrency: 'Valyutani tanlang.',
        validationItems: 'Kamida bitta qator qoshish.',
        validationProduct: 'Qatorda tovar tanlanmagan.',
        validationQuantity: 'Miqdor noldan katta bolishi kerak.',
        validationPrice: 'Narx noldan katta bolishi kerak.',
        missingUser: 'Talab qiluvchi uchun foydalanuvchi yoq.',
      },
      receive: {
        title: 'Tovarni qabul qilish',
        description: 'Qabul qilish manzil omboriga kirim xarid harakatini yozadi.',
        warehouse: 'Qabul qiluvchi ombor',
        warehouseHint: 'Tovarni qabul qiladigan ombor.',
        receiveAll: 'Barcha qolgan miqdorni qabul qilish',
        receiveAllHint: 'Server buyurtma boyicha kutilayotgan hammasini qabul qiladi.',
        specificHint: 'Haqiqatda kelgan miqdorni kiriting. Miqdor qolgan miqdordan oshmasligi kerak.',
        ordered: 'buyurtma',
        received: 'qabul qilingan',
        remaining: 'qolgan',
        now: 'Hozir qabul qilinadi',
        submit: 'Qabul qilish',
        submitting: 'Qabul qilinmoqda…',
        success: 'Tovar qabul qilindi. Buyurtma holati yangilandi.',
        validationRemaining: '{product}: kopi bilan {quantity} qabul qilish mumkin.',
        validationEmpty: 'Kamida bitta qator uchun miqdor kiriting.',
        lotTitle: 'Partiya',
        lotButton: 'Partiya',
        lotNumber: 'Partiya raqami',
        lotNumberPlaceholder: 'masalan LOT-2026-001',
        supplierBatch: 'Yetkazib beruvchi partiyasi',
        productionBatch: 'Ishlab chiqarish partiyasi',
        manufacturedAt: 'Ishlab chiqarilgan',
        expiresAt: 'Yaroqlilik muddati',
        qualityStatus: 'Sifat',
        lotHint: 'Ixtiyoriy. Mavjud partiya qayta ishlatiladi, aks holda shu malumotlar bilan yangisi yaratiladi.',
        lotNeedsQuantities: 'Partiya, batc yoki muddatni kiritish uchun qatorlar boyicha miqdor kiriting.',
        successWithDocument: 'Tovar qabul qilindi. {document} qabul hujjati yaratildi.',
      },
      lotQuality: {
        pending: 'Kutilmoqda',
        approved: 'Tasdiqlangan',
        rejected: 'Rad etilgan',
        quarantined: 'Karantin',
      },
      suppliers: {
        eyebrow: 'Xaridlar',
        title: 'Yetkazib beruvchilar',
        description: 'Xarid buyurtmalari uchun mavjud yetkazib beruvchilar.',
        searchPlaceholder: 'Yetkazib beruvchilarni qidirish…',
        count: '{count} yetkazib beruvchi',
        hint: 'Yetkazib beruvchilar reyestri Admin ilovasida yuritiladi.',
        empty: 'Yetkazib beruvchilar topilmadi.',
        name: 'Yetkazib beruvchi',
        code: 'ID',
      },
      deliveries: {
        eyebrow: 'Xaridlar',
        title: 'Yetkazib berishlar',
        description: 'Tovarni kutayotgan yoki qabul qilayotgan buyurtmalar.',
        searchPlaceholder: 'Raqam, yetkazib beruvchi yoki ombor boyicha qidirish…',
        onlyOpen: 'Faqat ochiq buyurtmalar',
        count: '{count} buyurtma',
        empty: 'Bu filtrlar boyicha yetkazib berish yoq.',
        order: 'Buyurtma',
        supplier: 'Yetkazib beruvchi',
        expected: 'Kutilmoqda',
        status: 'Holat',
        overdue: 'Kechikkan',
        dueSoon: 'Yaqin',
      },
      receipts: {
        title: 'Qabullar',
        count: '{count} harakat',
        hint: 'Bu buyurtma boyicha kirim xarid harakatlari.',
        date: 'Sana',
        item: 'Tovar',
        quantity: 'Miqdor',
        warehouse: 'Ombor',
        lot: 'Partiya',
        noLot: 'Partiyasiz',
        empty: 'Bu buyurtma boyicha hali hech narsa qabul qilinmagan.',
        document: 'Hujjat',
      },
      invoiceStatus: {
        open: 'Ochiq',
        partial: 'Qisman',
        closed: 'Yopilgan',
        void: 'Bekor qilingan',
      },
      invoices: {
        eyebrow: 'Xaridlar',
        title: 'Yetkazib beruvchi schotlari',
        description: 'Yetkazib beruvchilarga qarzlar va hisob-kitob holati.',
        searchPlaceholder: 'Raqam yoki yetkazib beruvchi boyicha qidirish…',
        allStatuses: 'Barcha holatlar',
        allSuppliers: 'Barcha yetkazib beruvchilar',
        allCurrencies: 'Barcha valyutalar',
        newInvoice: 'Yangi schot',
        count: '{count} schot',
        invoice: 'Schot',
        supplier: 'Yetkazib beruvchi',
        order: 'Xarid buyurtmasi',
        invoiceDate: 'Schot sanasi',
        dueDate: 'Tolov muddati',
        amount: 'Summa',
        status: 'Holat',
        open: 'Ochiq',
        overdue: 'Muddati otgan',
        void: 'Bekor qilish',
        empty: 'Bu filtrlar boyicha schot yoq.',
        hint: 'Hisob-kitob holati tolov taqsimotidan shakllanadi; qolda faqat bekor qilish mumkin.',
      },
      invoice: {
        back: 'Yetkazib beruvchi schotlari',
        edit: 'Tahrirlash',
        void: 'Bekor qilish',
        delete: 'Ochirish',
        summary: 'Malumot',
        supplier: 'Yetkazib beruvchi',
        order: 'Xarid buyurtmasi',
        invoiceNumber: 'Schot raqami',
        invoiceDate: 'Schot sanasi',
        dueDate: 'Tolov muddati',
        currency: 'Valyuta',
        exchangeRate: 'kurs',
        amount: 'Schot summasi',
        status: 'Holat',
        department: 'Bolim',
        warehouse: 'Ombor',
        notes: 'Izohlar',
        created: 'Yaratilgan',
        updated: 'Yangilangan',
        notFound: 'Schot topilmadi.',
        openOrder: 'Buyurtmani ochish',
        voidTitle: 'Schotni bekor qilish',
        voidConfirm: 'Schot bekor qilingan deb belgilanadi. Davom etilsinmi?',
        deleteTitle: 'Schotni ochirish',
        deleteConfirm: 'Schot ochiriladi. Davom etilsinmi?',
        voidSuccess: 'Schot bekor qilindi.',
      },
      invoiceForm: {
        createTitle: 'Yangi yetkazib beruvchi schoti',
        editTitle: '#{id} schotini tahrirlash',
        description: 'Yetkazib beruvchi schotini va agar mavjud bolsa, u yopadigan buyurtmani kiriting.',
        back: 'Yetkazib beruvchi schotlari',
        heading: 'Schot malumotlari',
        selectSupplier: 'Yetkazib beruvchini tanlang',
        selectWarehouse: 'Omborni tanlang',
        selectDepartment: 'Bolimni tanlang',
        supplier: 'Yetkazib beruvchi',
        purchaseOrder: 'Xarid buyurtmasi',
        purchaseOrderHint: 'Faqat tanlangan yetkazib beruvchining shu valyutadagi buyurtmalari mos keladi.',
        noOrders: 'Mos buyurtma yoq',
        invoiceNumber: 'Schot raqami',
        invoiceNumberPlaceholder: 'Masalan INV-ACME-1001',
        invoiceDate: 'Schot sanasi',
        dueDate: 'Tolov muddati',
        currency: 'Valyuta',
        exchangeRate: 'Ayirboshlash kursi',
        amount: 'Schot summasi',
        department: 'Bolim',
        warehouse: 'Ombor',
        notes: 'Izohlar',
        notesPlaceholder: 'Schot uchun ixtiyoriy izohlar',
        status: 'Holat',
        statusHint: 'Yangi schotlar ochiq holatda yaratiladi; hisob-kitob tolov taqsimotidan keladi.',
        voidHint: 'Bu formadan faqat bekor qilish mumkin; ochiq, qisman va yopilgan holatlar tolovlardan keladi.',
        create: 'Yaratish',
        update: 'Saqlash',
        validationSupplier: 'Yetkazib beruvchini tanlang.',
        validationCurrency: 'Valyutani tanlang.',
        validationNumber: 'Schot raqamini kiriting.',
        validationInvoiceDate: 'Schot sanasini kiriting.',
        validationDueDate: 'Tolov muddati schot sanasidan oldin bolishi mumkin emas.',
        validationAmount: 'Schot summasi noldan katta bolishi kerak.',
      },
      picker: {
        selectProduct: 'Tovarni tanlang',
        agentEyebrow: 'Agentlar',
        agentSearchLabel: 'Agentlarni qidirish',
        agentSearchPlaceholder: 'Nom yoki telefon boyicha qidirish…',
        agentEmpty: 'Agentlar topilmadi.',
        agentLoading: 'Agentlar yuklanmoqda…',
        searchLabel: 'Tovarlarni qidirish',
        searchPlaceholder: 'Nom yoki SKU boyicha qidirish…',
        empty: 'Tovarlar topilmadi.',
        loading: 'Tovarlar yuklanmoqda…',
        page: '{total} dan {page}-sahifa',
        close: 'Yopish',
        retry: 'Qayta urinish',
        previous: 'Oldingi',
        next: 'Keyingi',
        cancel: 'Bekor qilish',
      },
    },
  },
}
